import * as React from 'react';
import {
    Box,
    SwipeableDrawer,
    Typography,
    Divider,
    FormControl,
    TextField,
    Button,
    IconButton,
    Stack,
    Slider,
    MenuItem,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import Autocomplete from '@mui/material/Autocomplete';
import CloseIcon from '@mui/icons-material/Close';
import { useState } from 'react';

type RightDrawerProps = {
    open: boolean;
    onClose: () => void;
    fuelTypeData?: any[];
    TransmissionTypeData?: any[];
    bodyTypesData?: any[];
    brandData?: any[];
    dealerData?: any[]; // <-- Add this
};


const RightDrawer: React.FC<RightDrawerProps> = ({
    open,
    onClose,
    fuelTypeData = [],
    TransmissionTypeData = [],
    bodyTypesData = [],
    brandData = [],
    dealerData = [],
}) => {
    const theme = useTheme();
    const location = useLocation();
    const navigate = useNavigate();

    React.useEffect(() => {
        if (open) {
            initializeFiltersFromURL();
        }
    }, [open]);

    type SelectedFilters = {
        vehicleFuelTypeIds: string[];
        vehicleTransmissionTypeIds: string[];
        vehicleBodyTypeIds: string[];
        vehicleBrandIds: string[];
        dealerIds: string[];
        minAskingPrice: string;
        maxAskingPrice: string;
        minManufacturingYear: string;
        maxManufacturingYear: string;
        minMileage: string;
        maxMileage: string;
    };

    const [selectedFilters, setSelectedFilters] = React.useState<SelectedFilters>({
        vehicleFuelTypeIds: [],
        vehicleTransmissionTypeIds: [],
        vehicleBodyTypeIds: [],
        vehicleBrandIds: [],
        dealerIds: [],
        minAskingPrice: '',
        maxAskingPrice: '',
        minManufacturingYear: '',
        maxManufacturingYear: '',
        minMileage: '',
        maxMileage: '',
    });

    const [expandedTagsKey, setExpandedTagsKey] = useState<string | null>(null);
    const [controlledOpenKey, setControlledOpenKey] = useState<string | null>(null);

    const filtersConfig = [
        {
            label: 'Body Type',
            key: 'vehicleBodyTypeIds',
            data: bodyTypesData,
            idField: 'vehicleBodyTypeId',
            labelField: 'description',
        },
        {
            label: 'Brand',
            key: 'vehicleBrandIds',
            data: brandData,
            idField: 'vehicleBrandId',
            labelField: 'description',
        },
        {
            label: 'Fuel Type',
            key: 'vehicleFuelTypeIds',
            data: fuelTypeData,
            idField: 'vehicleFuelTypeId',
            labelField: 'description',
        },
        {
            label: 'Transmission',
            key: 'vehicleTransmissionTypeIds',
            data: TransmissionTypeData,
            idField: 'vehicleTransmissionTypeId',
            labelField: 'description',
        },
    ];

    const applyFilters = () => {
        const currentParams = new URLSearchParams(location.search);

        Object.entries(selectedFilters).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                if (value.length > 0) {
                    currentParams.set(key, value.join(','));
                } else {
                    currentParams.delete(key);
                }
            } else if (value !== '') {
                currentParams.set(key, String(value));
            } else {
                currentParams.delete(key);
            }
        });

        currentParams.delete('page');
        navigate(`/vehicle-listing?${currentParams.toString()}`);
        onClose();
    };

    const resetFilters = () => {
        setSelectedFilters({
            vehicleFuelTypeIds: [],
            vehicleTransmissionTypeIds: [],
            vehicleBodyTypeIds: [],
            dealerIds: [], // <-- Add this
            vehicleBrandIds: [],
            minAskingPrice: '',
            maxAskingPrice: '',
            minManufacturingYear: '',
            maxManufacturingYear: '',
            minMileage: '',
            maxMileage: '',
        });
        navigate('/vehicle-listing');
        onClose();
    };

    const renderAutocomplete = (
        label: string,
        key: keyof typeof selectedFilters,
        data: any[],
        idField: string,
        labelField: string,
        expandedTagsKey: string | null,
        setExpandedTagsKey: React.Dispatch<React.SetStateAction<string | null>>,
        controlledOpenKey: string | null,
        setControlledOpenKey: React.Dispatch<React.SetStateAction<string | null>>
    ) => {
        const isExpanded = expandedTagsKey === key;
        const selectedValues = data.filter((item) =>
            (selectedFilters[key] as string[]).includes(String(item[idField]))
        );

        if (!data || data.length === 0) return null; // Skip rendering if no options

        return (
            <FormControl size="small" fullWidth>
                <Typography variant="caption" fontWeight={500} mt={1} mb={0.5} color="text.secondary">
                    {label}
                </Typography>

                <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={data}
                    open={controlledOpenKey === key}
                    onOpen={() => setControlledOpenKey(key)}
                    onClose={() => {
                        if (expandedTagsKey === key) setExpandedTagsKey(null);
                        if (controlledOpenKey === key) setControlledOpenKey(null);
                    }}
                    value={selectedValues}
                    getOptionLabel={(option) => option[labelField]}
                    isOptionEqualToValue={(option, value) =>
                        String(option[idField]) === String(value[idField])
                    }
                    onChange={(_, newValue) =>
                        setSelectedFilters({
                            ...selectedFilters,
                            [key]: newValue.map((val) => String(val[idField])),
                        })
                    }
                    renderOption={(props, option, { selected }) => (
                        <li {...props}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <input
                                    type="checkbox"
                                    checked={selected}
                                    readOnly
                                    style={{ marginRight: 8 }}
                                />
                                {option[labelField]}
                            </Box>
                        </li>
                    )}
                    renderTags={(value) => {
                        const visibleTags = isExpanded ? value : value.slice(0, 1);
                        const remaining = value.length - visibleTags.length;

                        return (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {visibleTags.map((option, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            border: '1px solid #ccc',
                                            borderRadius: 2,
                                            px: 1,
                                            py: 0.25,
                                            bgcolor: '#f5f5f5',
                                            maxWidth: 120,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                        title={option[labelField]}
                                    >
                                        <Typography variant="caption">{option[labelField]}</Typography>
                                    </Box>
                                ))}

                                {!isExpanded && remaining > 0 && (
                                    <Box
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setExpandedTagsKey(key);
                                            setControlledOpenKey(key);
                                        }}
                                        role="button"
                                        aria-label={`Expand ${label} tags`}
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            border: '1px solid #ccc',
                                            borderRadius: 2,
                                            px: 1,
                                            py: 0.25,
                                            bgcolor: '#e0e0e0',
                                            cursor: 'pointer',
                                            '&:hover': { backgroundColor: '#d5d5d5' },
                                        }}
                                    >
                                        <Typography variant="caption">+{remaining} more</Typography>
                                    </Box>
                                )}
                            </Box>
                        );
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            size="small"
                            placeholder={
                                selectedFilters[key].length === 0 ? `Select ${label.toLowerCase()}` : ''
                            }
                        />
                    )}
                />
            </FormControl>
        );
    };



    const priceOptions = [
        { label: 'Under $ 30,000', min: 0, max: 30000 },
        { label: '$ 30,000 - 50,000', min: 30000, max: 50000 },
        { label: '$ 50,000 - 100,000', min: 50000, max: 100000 },
        { label: 'Above $ 100,000', min: 100000, max: null },
    ];

    const mileageOptions = [
        { label: 'Under 30,000 km', min: 0, max: 30000 },
        { label: '30,000 - 70,000 km', min: 30000, max: 70000 },
        { label: 'Above 70,000 km', min: 70000, max: null },
    ];

    const renderRangeAutocomplete = (
        label: string,
        options: { label: string; min: number; max: number | null }[],
        minKey: keyof typeof selectedFilters,
        maxKey: keyof typeof selectedFilters
    ) => {
        const currentValue = options.find(
            (opt) =>
                selectedFilters[minKey] === opt.min.toString() &&
                selectedFilters[maxKey] === (opt.max !== null ? opt.max.toString() : '')
        ) || null;


        return (
            <FormControl size="small" fullWidth>
                <Typography
                    variant="caption"
                    fontWeight={500}
                    mt={1}
                    mb={0.5}
                    color="text.secondary"
                >
                    {label}
                </Typography>
                <Autocomplete
                    options={options}
                    getOptionLabel={(option) => option.label}
                    value={currentValue}
                    onChange={(_, newValue) => {
                        if (newValue) {
                            setSelectedFilters((prev) => ({
                                ...prev,
                                [minKey]: newValue.min.toString(),
                                [maxKey]: newValue.max !== null ? newValue.max.toString() : '',
                            }));
                        } else {
                            setSelectedFilters((prev) => ({
                                ...prev,
                                [minKey]: '',
                                [maxKey]: '',
                            }));
                        }
                    }}
                    isOptionEqualToValue={(option, value) => option.label === value.label}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            size="small"
                            placeholder={`Select ${label.toLowerCase()}`}
                        />
                    )}
                />
            </FormControl>
        );
    };



    const renderSlider = (
        label: string,
        minKey: keyof typeof selectedFilters,
        maxKey: keyof typeof selectedFilters,
        min: number,
        max: number,
        step: number
    ) => {
        const minValue = Number(selectedFilters[minKey]) || min;
        const maxValue = Number(selectedFilters[maxKey]) || max;

        return (
            <FormControl fullWidth>
                <Typography variant="caption" fontWeight={500} mt={1} mb={0.5} color="text.secondary">
                    {label}
                </Typography>
                <Slider
                    value={[minValue, maxValue]}
                    min={min}
                    max={max}
                    step={step}
                    valueLabelDisplay="auto"
                    onChange={(_, newValue) => {
                        const [newMin, newMax] = newValue as number[];
                        setSelectedFilters((prev) => ({
                            ...prev,
                            [minKey]: newMin.toString(),
                            [maxKey]: newMax.toString(),
                        }));
                    }}
                />
            </FormControl>
        );
    };


    const renderDealerFilter = (
        label: string,
        key: keyof typeof selectedFilters,
        data: { dealerCompanyId: number; dealerCompanyName: string; imageUrl?: string }[]
    ) => (
        <FormControl size="small" fullWidth>
            <Typography
                variant="caption"
                fontWeight={500}
                mt={1}
                mb={0.5}
                color="text.secondary"
            >
                {label}
            </Typography>

            <Autocomplete
                multiple
                disableCloseOnSelect
                options={data}
                getOptionLabel={(option) => option.dealerCompanyName}
                value={data.filter((item) =>
                    (selectedFilters[key] as string[]).includes(String(item.dealerCompanyId))
                )}
                onChange={(_, newValue) =>
                    setSelectedFilters((prev) => ({
                        ...prev,
                        [key]: newValue.map((val) => String(val.dealerCompanyId)),
                    }))
                }
                isOptionEqualToValue={(option, value) =>
                    String(option.dealerCompanyId) === String(value.dealerCompanyId)
                }
                renderOption={(props, option) => (
                    <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                            component="img"
                            src={option.imageUrl}
                            alt={option.dealerCompanyName}
                            sx={{
                                width: 30,
                                height: '100%',
                                objectFit: 'fill',
                            }}
                        />
                        <Typography>{option.dealerCompanyName}</Typography>
                    </Box>
                )}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                border: '1px solid #ccc',
                                borderRadius: 2,
                                px: 1,
                                py: 0.5,
                                mr: 0.5,
                                bgcolor: '#fafafa',
                            }}
                            {...getTagProps({ index })}
                        >
                            <Box
                                component="img"
                                src={option.imageUrl}
                                alt={option.dealerCompanyName}
                                sx={{
                                    width: 30,
                                    height: '100%',
                                    objectFit: 'fill',
                                }}
                            />
                            <Typography variant="caption">{option.dealerCompanyName}</Typography>
                        </Box>
                    ))
                }
                renderInput={(params) => (
                    <TextField
                        {...params}
                        size="small"
                        placeholder={
                            (selectedFilters[key] as string[]).length === 0
                                ? `Select ${label.toLowerCase()}`
                                : ''
                        }
                    />
                )}

            />
        </FormControl>
    );

    const initializeFiltersFromURL = () => {
        const searchParams = new URLSearchParams(location.search);

        const getArrayParam = (key: string) =>
            searchParams.get(key)?.split(',').map((v) => v.trim()) || [];

        setSelectedFilters({
            vehicleFuelTypeIds: getArrayParam('vehicleFuelTypeIds'),
            vehicleTransmissionTypeIds: getArrayParam('vehicleTransmissionTypeIds'),
            vehicleBodyTypeIds: getArrayParam('vehicleBodyTypeIds'),
            vehicleBrandIds: getArrayParam('vehicleBrandIds'),
            dealerIds: getArrayParam('dealerIds'),
            minAskingPrice: searchParams.get('minAskingPrice') || '',
            maxAskingPrice: searchParams.get('maxAskingPrice') || '',
            minManufacturingYear: searchParams.get('minManufacturingYear') || '',
            maxManufacturingYear: searchParams.get('maxManufacturingYear') || '',
            minMileage: searchParams.get('minMileage') || '',
            maxMileage: searchParams.get('maxMileage') || '',
        });
    };

    return (
        <SwipeableDrawer anchor="right" open={open} onClose={onClose} onOpen={initializeFiltersFromURL}>

            <Box sx={{ width: 350, height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Header */}
                <Box
                    px={2}
                    py={1.5}
                    sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        bgcolor: '#fff',
                        borderBottom: '1px solid #eee',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography variant="subtitle1" fontWeight="bold">
                        Filter Vehicles
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Filter Area */}
                <Box sx={{ px: 2, py: 1, flexGrow: 1, overflowY: 'auto' }}>
                    {renderDealerFilter('Dealer', 'dealerIds', dealerData)}
                    <Stack spacing={1.5}>
                        {filtersConfig.map((config) => (
                            <div key={config.key}>
                                {renderAutocomplete(
                                    config.label,
                                    config.key as keyof typeof selectedFilters,
                                    config.data,
                                    config.idField,
                                    config.labelField,
                                    expandedTagsKey,
                                    setExpandedTagsKey,
                                    controlledOpenKey,
                                    setControlledOpenKey
                                )}
                            </div>
                        ))}
                        {renderRangeAutocomplete('Asking Price', priceOptions, 'minAskingPrice', 'maxAskingPrice')}
                        {renderRangeAutocomplete('Mileage', mileageOptions, 'minMileage', 'maxMileage')}

                        {renderSlider('Manufacturing Year', 'minManufacturingYear', 'maxManufacturingYear', 2010, new Date().getFullYear(), 1)}

                    </Stack>
                </Box>

                {/* Footer Buttons */}
                <Box px={2} py={1.5} borderTop="1px solid #eee">
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="outlined"
                            color="secondary"
                            fullWidth
                            size="small"
                            onClick={resetFilters}
                        >
                            Reset
                        </Button>
                        <Button
                            variant="contained"
                            fullWidth
                            size="small"
                            onClick={applyFilters}
                        >
                            Apply
                        </Button>
                    </Stack>
                </Box>
            </Box>
        </SwipeableDrawer>
    );
};

export default RightDrawer;
